import { submitPost, type SubmitPostRequestParams } from "deso-protocol";
import { use } from "react";

import { retry, waitForTxn } from "@/backend/api-helpers";
import { getPostByHash } from "@/components/app/server-actions";
import { useToast } from "@/components/ui/use-toast";
import type { PostType } from "@/contexts/feed-context";
import { UserContext } from "@/contexts/user-context";
import { useUpvotePost } from "@/hooks-api/use-upvote-post";

type SubmitPostTransaction = {
  postHashHex?: string;
  transactionHash: string;
};

type SubmitPostOptions = {
  onTransactionSubmitted?: (
    transaction: SubmitPostTransaction,
  ) => Promise<void> | void;
  toleratePostHydrationFailure?: boolean;
};

type StrictSubmitPostOptions = SubmitPostOptions & {
  toleratePostHydrationFailure?: false;
};

type TolerantSubmitPostOptions = SubmitPostOptions & {
  toleratePostHydrationFailure: true;
};

export const useSubmitPost = () => {
  const { upvotePost } = useUpvotePost();
  const { toast } = useToast();

  const {
    wealth,
    globalVal: { maxPostContributionWealthUsdCents },
  } = use(UserContext);

  const userCappedWealth = Math.min(
    wealth?.totalBalanceUsdCents ?? 0,
    maxPostContributionWealthUsdCents,
  );

  async function submitPostFn(
    postRequestParams: SubmitPostRequestParams,
    options?: StrictSubmitPostOptions,
  ): Promise<PostType>;
  async function submitPostFn(
    postRequestParams: SubmitPostRequestParams,
    options: TolerantSubmitPostOptions,
  ): Promise<PostType | null>;
  async function submitPostFn(
    postRequestParams: SubmitPostRequestParams,
    options: SubmitPostOptions = {},
  ): Promise<PostType | null> {
    let transactionSubmitted = false;

    try {
      const edit = "PostHashHexToModify" in postRequestParams;
      const data = await submitPost(postRequestParams);

      if (!data.submittedTransactionResponse?.TxnHashHex) {
        throw new Error("Transaction was not constructed. Please try again.");
      }

      const transactionHash = data.submittedTransactionResponse.TxnHashHex;
      const postHash =
        data.submittedTransactionResponse.PostEntryResponse?.PostHashHex;

      transactionSubmitted = true;

      await options.onTransactionSubmitted?.({
        postHashHex: postHash,
        transactionHash,
      });

      const promises = [];

      if (postHash && !edit) {
        promises.push(
          upvotePost({ postHash }).catch((e) => {
            console.error("Submit post Upvote failed", e);
          }),
        );
      }

      promises.push(
        waitForTxn({
          transactionHash,
        }),
      );

      await Promise.all(promises);

      const postHashHex =
        data.submittedTransactionResponse.PostEntryResponse?.PostHashHex;

      if (!postHashHex) {
        throw new Error("Post was not created properly. Please try again.");
      }

      const post = await retry({
        action: () => getPostByHash(postHashHex),
        checkSuccess: (result) => !!result?.postHash,
      });

      if (!post) {
        throw new Error("New post cannot be fetched. Please try again.");
      }

      if (!edit) {
        post.postStats ??= {
          totalReactionCount: 1,
          positiveReactionCount: 1,
          likeReactionCount: 1,
          upvoteWealthTotalUsdCentsCapped: userCappedWealth,
        };
      }

      return post as PostType;
    } catch (error) {
      if (transactionSubmitted && options.toleratePostHydrationFailure) {
        console.error("Post submitted, but post hydration failed", error);
        return null;
      }

      toast({
        title: "Error",
        description:
          "There was an error submitting your post. Please try again.",
        variant: "destructive",
      });
      throw error;
    }
  }

  return submitPostFn;
};
